import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MilestoneBadge from "@/components/MilestoneBadge";
import { MessageCircle, X, Send as SendIcon, ShieldCheck, Star, Trash2, Smile, Crown } from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import UserProfileDialog from "@/components/UserProfileDialog";
import EmojiPickerBar from "@/components/EmojiPickerBar";

const STORAGE_KEY = "ts_community_fab_pos_v1";
const FAB_SIZE = 56;
const MARGIN = 12;
const BOTTOM_NAV_H = 68;   // reserve space for mobile bottom nav so FAB doesn't overlap Profile tab
const MOBILE_BP = 768;     // Tailwind md breakpoint — desktop has no bottom-nav
const POLL_MS = 4000;
const EMOJIS = ["👍", "🔥", "❤️", "😂"];

function bottomReserve() {
  if (typeof window === "undefined") return MARGIN;
  return window.innerWidth < MOBILE_BP ? BOTTOM_NAV_H + MARGIN : MARGIN;
}

function clampPos(x, y) {
  const w = typeof window !== "undefined" ? window.innerWidth : 360;
  const h = typeof window !== "undefined" ? window.innerHeight : 640;
  const bot = bottomReserve();
  return {
    x: Math.min(Math.max(x, MARGIN), w - FAB_SIZE - MARGIN),
    y: Math.min(Math.max(y, MARGIN), h - FAB_SIZE - bot),
  };
}

function loadPos() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return clampPos(...JSON.parse(raw));
  } catch (err) { logDev(err); }
  const w = window.innerWidth, h = window.innerHeight;
  return { x: w - FAB_SIZE - 24, y: h - FAB_SIZE - bottomReserve() - 12 };
}

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday
      ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString([], { month: "short", day: "numeric" }) + " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function initial(name) {
  const s = (name || "?").trim();
  return s.charAt(0).toUpperCase();
}

function useFabAutoHide() {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let lastY = window.scrollY;
    const mobile = () => window.innerWidth < MOBILE_BP;
    const isField = (el) => el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) && !el.closest('[data-testid="community-chat-panel"]');
    const onScroll = () => {
      const y = window.scrollY;
      if (mobile() && Math.abs(y - lastY) > 8) setHidden(y > lastY && y > 80);
      lastY = y;
    };
    let restoreT;
    const onFocusIn = (e) => { if (mobile() && isField(e.target)) { clearTimeout(restoreT); setHidden(true); } };
    const onFocusOut = (e) => { if (isField(e.target)) { clearTimeout(restoreT); restoreT = setTimeout(() => setHidden(false), 400); } };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  return hidden;
}

function MessageBubble({ msg, isSelf, isChampion, milestone, canDelete, currentUserId, onDelete, onReact, onOpenUser }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const bubbleCls = isSelf
    ? "bg-[#00b4ff] text-white rounded-2xl rounded-br-md"
    : "bg-slate-100 text-slate-900 rounded-2xl rounded-bl-md";
  const reactions = msg.reactions || {};
  const reactionEntries = Object.entries(reactions).filter(([, users]) => (users || []).length > 0);

  const toggle = (emoji) => { onReact(msg.id, emoji); setPickerOpen(false); };

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const close = (e) => { if (e.type === "keydown" ? e.key === "Escape" : !e.target.closest(`[data-testid="chat-msg-picker-${msg.id}"], [data-testid="chat-msg-react-${msg.id}"]`)) setPickerOpen(false); };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", close); };
  }, [pickerOpen, msg.id]);

  return (
    <div className={`flex gap-2 ${isSelf ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${msg.id}`}>
      {!isSelf && (
        <UserAvatar
          path={msg.profile_picture_path}
          size={32}
          className={`${msg.is_admin ? "bg-slate-900 text-white" : msg.is_master ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white" : "bg-slate-200 text-slate-700"} text-xs font-bold`}
          testId={`chat-msg-avatar-${msg.id}`}
          fallback={msg.is_admin ? <ShieldCheck size={14} /> : msg.is_master ? <Star size={12} className="fill-white" /> : initial(msg.user_name)}
          onClick={onOpenUser ? () => onOpenUser(msg.user_id, "profile") : undefined}
        />
      )}
      <div className={`max-w-[75%] group ${isSelf ? "items-end" : "items-start"} flex flex-col gap-1`}>
        {!isSelf && (
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500 flex items-center gap-1 px-1">
            <button
              type="button"
              data-testid={`chat-msg-name-${msg.id}`}
              onClick={() => onOpenUser && onOpenUser(msg.user_id, "dm")}
              className="truncate max-w-[140px] hover:text-[#00b4ff] hover:underline"
              style={{ transition: "color 0.2s ease" }}
            >
              {msg.user_name || "member"}
            </button>
            {msg.is_admin && <span className="px-1.5 py-0.5 rounded bg-slate-900 text-white text-[8px]">ADMIN</span>}
            {msg.is_master && !msg.is_admin && <span className="px-1.5 py-0.5 rounded bg-amber-500 text-white text-[8px]">MASTER</span>}
            <MilestoneBadge milestone={milestone} testId={`chat-msg-milestone-${msg.id}`} />
            {isChampion && (
              <span data-testid={`chat-msg-crown-${msg.id}`} title="Juara Daily Quiz minggu lepas" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gradient-to-r from-yellow-300 to-amber-500 text-white text-[8px]">
                <Crown size={9} className="fill-white" /> QUIZ KING
              </span>
            )}
          </div>
        )}
        <div className="relative">
          <div className={`px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${bubbleCls}`} style={{ overflowWrap: "anywhere" }}>
            {msg.text}
          </div>
          {/* React button — appears on hover */}
          <button
            data-testid={`chat-msg-react-${msg.id}`}
            onClick={() => setPickerOpen((p) => !p)}
            className={`absolute -top-3 ${isSelf ? "-left-3" : "-right-3"} w-7 h-7 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-[#00b4ff] hover:border-[#00b4ff] shadow-sm grid place-items-center opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100`}
            style={{ transition: "opacity 0.15s ease, color 0.15s ease, border-color 0.15s ease" }}
            aria-label="React"
          >
            <Smile size={13} />
          </button>
          {pickerOpen && (
            <div
              data-testid={`chat-msg-picker-${msg.id}`}
              className={`absolute z-10 bottom-full mb-1 ${isSelf ? "right-0" : "left-0"} bg-white border border-slate-200 rounded-full shadow-lg px-2 py-1 flex gap-1 max-w-[calc(100vw-4rem)]`}
            >
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => toggle(e)}
                  data-testid={`chat-msg-react-${msg.id}-${e}`}
                  className="text-xl w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100 hover:scale-125"
                  style={{ transition: "transform 0.15s ease, background-color 0.15s ease" }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
        {reactionEntries.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-0.5 ${isSelf ? "justify-end" : ""}`}>
            {reactionEntries.map(([emoji, users]) => {
              const mine = (users || []).includes(currentUserId);
              return (
                <button
                  key={emoji}
                  onClick={() => toggle(emoji)}
                  data-testid={`chat-msg-reaction-${msg.id}-${emoji}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono-plex border ${mine ? "bg-[#00b4ff]/10 border-[#00b4ff]/50 text-[#00b4ff]" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}
                  style={{ transition: "background-color 0.15s ease, border-color 0.15s ease" }}
                >
                  <span>{emoji}</span>
                  <span className="font-semibold">{users.length}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className={`flex items-center gap-2 text-[9px] font-mono-plex tracking-widest uppercase text-slate-400 px-1 ${isSelf ? "flex-row-reverse" : ""}`}>
          <span>{fmtTime(msg.created_at)}</span>
          {canDelete && (
            <button
              data-testid={`chat-msg-delete-${msg.id}`}
              onClick={() => onDelete(msg.id)}
              className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600"
              style={{ transition: "color 0.15s ease, opacity 0.15s ease" }}
              aria-label="Delete message"
            >
              <Trash2 size={10} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommunityChat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [ready, setReady] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [profileUserId, setProfileUserId] = useState(null);
  const [profileMode, setProfileMode] = useState("profile");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const stateRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0, moved: false, pointerId: null });
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const lastSeenRef = useRef(null);
  const pollRef = useRef(null);
  const [champions, setChampions] = useState([]);
  const [milestones, setMilestones] = useState({});
  const fabHidden = useFabAutoHide();

  const openUser = useCallback((userId, mode = "profile") => {
    if (!userId || userId === user?.id) return;
    setProfileMode(mode);
    setProfileUserId(userId);
  }, [user?.id]);

  const insertEmoji = useCallback((emoji) => {
    const el = inputRef.current;
    if (!el) { setDraft((d) => d + emoji); return; }
    const s = el.selectionStart ?? draft.length;
    const e = el.selectionEnd ?? draft.length;
    const next = draft.slice(0, s) + emoji + draft.slice(e);
    setDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = s + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  }, [draft]);

  useEffect(() => {
    setPos(loadPos());
    setReady(true);
    const onResize = () => setPos((p) => clampPos(p.x, p.y));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    api.get("/quiz/champion").then(({ data }) => setChampions(data.user_ids || [])).catch(() => {});
    api.get("/quiz/milestones").then(({ data }) => setMilestones(data || {})).catch(() => {});
  }, [user?.id]);

  // Load initial messages once mounted (so unread counter tracks correctly).
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data } = await api.get("/community/messages?limit=50");
        setMessages(data);
        lastSeenRef.current = data.length ? data[data.length - 1].created_at : new Date().toISOString();
      } catch { /* transient — user might just not be approved yet */ }
    })();
  }, [user?.id]);

  // Polling for new messages while chat is open OR always at slower rate for badge.
  useEffect(() => {
    const tick = async () => {
      try {
        const after = lastSeenRef.current;
        const { data } = await api.get(`/community/messages?after=${encodeURIComponent(after || "")}&limit=100`);
        if (data.length > 0) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const merged = [...prev, ...data.filter((m) => !seen.has(m.id))];
            return merged.slice(-200);
          });
          lastSeenRef.current = data[data.length - 1].created_at;
          if (!open) {
            const incoming = data.filter((m) => m.user_id !== user?.id);
            if (incoming.length) setUnread((u) => Math.min(u + incoming.length, 99));
          }
        }
      } catch { /* ignore */ }
    };
    pollRef.current = setInterval(tick, open ? POLL_MS : POLL_MS * 3);
    return () => clearInterval(pollRef.current);
  }, [open, user?.id]);

  // Auto scroll to bottom when new messages or opening panel.
  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
      setUnread(0);
    }
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/community/messages", { text });
      setMessages((prev) => [...prev, data]);
      lastSeenRef.current = data.created_at;
      setDraft("");
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.warning(err.response?.data?.detail || "Slow down!");
      else toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const deleteMsg = async (id) => {
    try {
      await api.delete(`/community/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const reactMsg = async (id, emoji) => {
    // Optimistic toggle for snappy UX; server truth arrives via next poll.
    setMessages((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const reactions = { ...(m.reactions || {}) };
      const users = [...(reactions[emoji] || [])];
      const idx = users.indexOf(user.id);
      if (idx >= 0) users.splice(idx, 1);
      else users.push(user.id);
      if (users.length) reactions[emoji] = users;
      else delete reactions[emoji];
      return { ...m, reactions };
    }));
    try {
      await api.post(`/community/messages/${id}/react`, { emoji });
    } catch { toast.error("Reaction failed"); }
  };

  // ----- draggable floating button handlers -----
  const onPointerDown = useCallback((e) => {
    const el = e.currentTarget;
    stateRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: pos.x, origY: pos.y,
      moved: false, pointerId: e.pointerId,
    };
    setDragging(true);
    el.setPointerCapture?.(e.pointerId);
  }, [pos.x, pos.y]);

  const onPointerMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - stateRef.current.startX;
    const dy = e.clientY - stateRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) stateRef.current.moved = true;
    setPos(clampPos(stateRef.current.origX + dx, stateRef.current.origY + dy));
  }, [dragging]);

  const onPointerUp = useCallback((e) => {
    if (!dragging) return;
    setDragging(false);
    const el = e.currentTarget;
    el.releasePointerCapture?.(e.pointerId);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([pos.x, pos.y])); } catch (err) { logDev(err); }
  }, [dragging, pos.x, pos.y]);

  const onFabClick = useCallback((e) => {
    if (stateRef.current.moved) { e.preventDefault(); e.stopPropagation(); return; }
    setOpen((o) => !o);
  }, []);

  if (!ready || !user) return null;

  return (
    <>
      <button
        type="button"
        data-testid="community-chat-btn"
        aria-label="Open community chat"
        draggable={false}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={onFabClick}
        className="fixed z-50 grid place-items-center rounded-full bg-[#00b4ff] hover:bg-[#0090cc] text-white shadow-[0_12px_40px_-8px_rgba(0,180,255,0.7)] active:scale-95 select-none border-0"
        style={{
          left: pos.x, top: pos.y, width: FAB_SIZE, height: FAB_SIZE,
          touchAction: "none",
          cursor: dragging ? "grabbing" : "grab",
          opacity: fabHidden && !open ? 0 : 1,
          pointerEvents: fabHidden && !open ? "none" : "auto",
          transform: fabHidden && !open ? "scale(0.6)" : undefined,
          transition: dragging ? "none" : "background-color 0.2s ease, transform 0.2s ease, opacity 0.2s ease",
        }}
      >
        <MessageCircle size={22} className="pointer-events-none" />
        {unread > 0 && (
          <span
            data-testid="community-chat-unread"
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-mono-plex font-bold grid place-items-center border-2 border-white pointer-events-none"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="community-chat-panel"
          className="fixed z-50 flex flex-col bg-white shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] border border-slate-200 overflow-hidden"
          style={{
            right: 16, left: 16, bottom: 16, top: 16,
            maxWidth: 420, marginLeft: "auto",
            borderRadius: 20,
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-white">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-[#00b4ff] grid place-items-center text-white">
                <MessageCircle size={16} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold tracking-tight text-sm">Community Chat</div>
                <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500">Live · approved members</div>
              </div>
            </div>
            <button
              data-testid="community-chat-close"
              onClick={() => setOpen(false)}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
              style={{ transition: "background-color 0.2s ease" }}
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50" style={{ overscrollBehavior: "contain" }}>
            {messages.length === 0 ? (
              <div className="h-full grid place-items-center text-center text-slate-400 text-sm">
                <div>
                  <MessageCircle size={32} className="mx-auto opacity-30" />
                  <div className="mt-3 font-mono-plex tracking-widest uppercase text-[10px]">Belum ada mesej</div>
                  <div className="mt-1 text-xs">Jadi yang pertama — sapa community!</div>
                </div>
              </div>
            ) : messages.map((m) => (
              <MessageBubble
                key={m.id}
                msg={m}
                isSelf={m.user_id === user.id}
                isChampion={champions.includes(m.user_id)}
                milestone={milestones[m.user_id]}
                currentUserId={user.id}
                canDelete={isAdmin || m.user_id === user.id}
                onDelete={deleteMsg}
                onReact={reactMsg}
                onOpenUser={openUser}
              />
            ))}
          </div>

          {/* Composer */}
          <form onSubmit={send} className="border-t border-slate-200 bg-white px-3 py-3 space-y-2">
            {emojiOpen && <EmojiPickerBar onPick={insertEmoji} testId="community-emoji-bar" />}
            <div className="flex items-end gap-2">
              <button
                type="button"
                data-testid="community-emoji-toggle"
                onClick={() => setEmojiOpen((o) => !o)}
                className={`grid place-items-center w-10 h-10 rounded-full ${emojiOpen ? "bg-[#00b4ff]/10 text-[#00b4ff]" : "bg-slate-100 text-slate-500 hover:text-[#00b4ff]"} shrink-0`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
                aria-label="Emoji"
              >
                <Smile size={18} />
              </button>
              <textarea
                ref={inputRef}
                data-testid="community-chat-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                placeholder="Ketik mesej…"
                maxLength={500}
                rows={1}
                className="flex-1 resize-none max-h-24 px-3 py-2.5 rounded-2xl bg-slate-100 focus:bg-white border border-transparent focus:border-[#00b4ff] focus:outline-none text-sm leading-relaxed"
                style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}
              />
              <button
                data-testid="community-chat-send"
                type="submit"
                disabled={!draft.trim() || sending}
                className="grid place-items-center w-10 h-10 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-50 disabled:cursor-not-allowed text-white shrink-0"
                style={{ transition: "background-color 0.2s ease" }}
                aria-label="Send"
              >
                <SendIcon size={16} className="translate-x-[1px] -translate-y-[1px]" />
              </button>
            </div>
          </form>
        </div>
      )}
      <UserProfileDialog
        userId={profileUserId}
        open={!!profileUserId}
        onOpenChange={(o) => { if (!o) setProfileUserId(null); }}
        initialMode={profileMode}
      />
    </>
  );
}
